import { API_URL } from '@/config/app';
import { logout, updateTokens } from '@/store/authSlice';
import { getAppStore } from '@/store/storeInstance';
import { isApiResponse } from '@/types/api';
import { createAccessToken } from '@/utils/agent';

const REFRESH_BUFFER_MS = 2 * 60 * 1000;

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

let refreshPromise: Promise<AuthTokens | null> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function decodeBase64Url(value: string): string {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = normalized.length % 4 === 0 ? '' : '='.repeat(4 - (normalized.length % 4));
  return atob(`${normalized}${padding}`);
}

export function getAccessTokenExpiry(accessToken: string): number | null {
  try {
    const segment = accessToken.split('.')[1];
    if (!segment) return null;
    const payload = JSON.parse(decodeBase64Url(segment)) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function isAccessTokenExpired(accessToken: string, bufferMs = 0): boolean {
  const expiry = getAccessTokenExpiry(accessToken);
  if (!expiry) return false;
  return Date.now() >= expiry - bufferMs;
}

export function clearRefreshTimer(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

export async function refreshAuthTokens(): Promise<AuthTokens | null> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const { refreshToken, isAuthenticated } = getAppStore().getState().auth;
    if (!isAuthenticated || !refreshToken) {
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': createAccessToken(),
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        return null;
      }

      const data: unknown = await response.json();
      const tokens = isApiResponse<AuthTokens>(data) ? data.data : (data as AuthTokens);

      if (!tokens?.accessToken || !tokens?.refreshToken) {
        return null;
      }

      getAppStore().dispatch(updateTokens(tokens));
      scheduleAccessTokenRefresh(tokens.accessToken);
      return tokens;
    } catch {
      return null;
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

export async function getValidAccessToken(): Promise<string | null> {
  const { accessToken, isAuthenticated } = getAppStore().getState().auth;
  if (!isAuthenticated || !accessToken) {
    return null;
  }

  if (!isAccessTokenExpired(accessToken, REFRESH_BUFFER_MS)) {
    return accessToken;
  }

  const tokens = await refreshAuthTokens();
  return tokens?.accessToken ?? null;
}

export async function authenticatedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = path.startsWith('http') ? path : `${API_URL}${path}`;

  const execute = async (accessToken: string) =>
    fetch(url, {
      ...init,
      headers: {
        ...init.headers,
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        'x-access-token': createAccessToken(),
      },
    });

  const accessToken = await getValidAccessToken();
  if (!accessToken) {
    getAppStore().dispatch(logout());
    throw new Error('Session expired.');
  }

  let response = await execute(accessToken);
  if (response.status !== 401) {
    return response;
  }

  const tokens = await refreshAuthTokens();
  if (!tokens?.accessToken) {
    getAppStore().dispatch(logout());
    throw new Error('Session expired.');
  }

  response = await execute(tokens.accessToken);
  if (response.status === 401) {
    getAppStore().dispatch(logout());
    throw new Error('Session expired.');
  }

  return response;
}

export function scheduleAccessTokenRefresh(accessToken: string | null): void {
  clearRefreshTimer();
  if (!accessToken) return;

  const expiry = getAccessTokenExpiry(accessToken);
  if (!expiry) return;

  const delay = Math.max(expiry - REFRESH_BUFFER_MS - Date.now(), 10_000);

  refreshTimer = setTimeout(() => {
    void refreshAuthTokens();
  }, delay);
}

export async function ensureFreshSession(): Promise<void> {
  const { accessToken, isAuthenticated } = getAppStore().getState().auth;
  if (!isAuthenticated || !accessToken) return;

  if (isAccessTokenExpired(accessToken, REFRESH_BUFFER_MS)) {
    await refreshAuthTokens();
    return;
  }

  scheduleAccessTokenRefresh(accessToken);
}
